import type { BoardDoc } from '../doc/BoardDoc';
import { api } from '../sync/api';
import { deleteAssetLocally } from './assets/localAssets';

/** Server-side storage of a board: what it holds and what nothing references any more. */
export interface SweepResult {
  total: { count: number; bytes: number };
  unused: { count: number; bytes: number };
  /** Asset ids removed (empty for a dry run). */
  removed: string[];
}

/** Asset ids the document still references (image and file items on every layer). */
export function referencedAssetIds(board: BoardDoc): string[] {
  const ids = new Set<string>();
  for (const item of board.index.all()) {
    if (item.type === 'image' || item.type === 'file') ids.add(item.props.assetId);
  }
  return [...ids];
}

/**
 * Reports (apply=false) or removes (apply=true) the board's unused server assets. The server
 * spares uploads younger than an hour. After a removal the undo history is cleared: bringing a
 * deleted image back would point at a file that no longer exists.
 */
export async function sweepUnusedAssets(board: BoardDoc, apply: boolean): Promise<SweepResult> {
  const res = await api.post<SweepResult>(`/boards/${board.boardId}/assets/sweep`, { keep: referencedAssetIds(board), apply });
  if (apply && res.removed.length) {
    await Promise.all(res.removed.map((id) => deleteAssetLocally(id)));
    board.undo.clear();
  }
  return res;
}
